import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTeacherData } from '../../context/TeacherDataContext';
import { sheetsClient } from '../../data/sheetsClient';
import { Card, Button, TabBar, Skeleton, EmptyState } from '../shared/UI';
import Badge, { LockBadge } from '../shared/Badge';
import PaymentPendingNote from '../shared/PaymentPendingNote';
import { INCLUDED_STAFF_COUNT, ADDITIONAL_STAFF_FEE, formatCurrency } from '../../utils/feeCalculations';

const TABS = ['Invite link', 'Roster'];

export default function StaffChaperones() {
  const { school, staff, timeline, feeConfig, locks, loading, refetch, patch } = useTeacherData();
  // ENTRY_RATES is the raw Prices-tab map (see getFeeConfig) — falls back to
  // the same default feeCalculations.js uses until a sheet has an
  // "Additional Staff Fee" row.
  const additionalStaffFee = feeConfig?.ENTRY_RATES?.['Additional Staff Fee'] ?? ADDITIONAL_STAFF_FEE;
  const [activeTab, setActiveTab] = useState('Invite link');
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [emailMenuOpen, setEmailMenuOpen] = useState(false);
  const [savingPrefsId, setSavingPrefsId] = useState(null);
  const [prefsError, setPrefsError] = useState('');

  // Once the roster loads, default to the Roster tab when there's actually
  // someone registered — but only that first time, so switching tabs
  // afterward never gets overridden.
  const hasAutoSelectedTab = useRef(false);
  useEffect(() => {
    if (!hasAutoSelectedTab.current && staff) {
      hasAutoSelectedTab.current = true;
      if (staff.length > 0) setActiveTab('Roster');
    }
  }, [staff]);

  // Flagged by the backend (isStaffRegistrationTask) rather than matched by
  // exact title text here — schools word this Timeline row differently
  // ("Staff & Chaperones Registration", "Teacher/Chaperone Registration",
  // etc.), and an exact-string miss used to silently break this toggle.
  const staffRegRow = (timeline || []).find((s) => s.isStaffRegistrationRow);
  const isStaffRegistrationComplete = staffRegRow?.status === 'complete';
  // Each school needs at least one workshop supervisor.
  const hasSupervisor = (staff || []).some((s) => s.workshopSupervisor);

  // Toggling either checkbox on the Roster page is a preference the
  // registering teacher sets on that person's behalf — not something the
  // staff member submits themselves. Applied optimistically (patch.staff,
  // not refetch.staff) for two reasons: it's instant instead of waiting on
  // a round trip, and it avoids the loading-skeleton flash a full
  // refetch.staff() would cause (this page renders that skeleton whenever
  // loading.staff is true). Reverted on failure.
  const applyStaffField = (staffId, field, value) =>
    patch.staff((prev) => prev.map((s) => (s.id === staffId ? { ...s, [field]: value } : s)));

  const handleTogglePreference = async (staffId, field, value) => {
    setSavingPrefsId(staffId);
    setPrefsError('');
    applyStaffField(staffId, field, value);
    try {
      await sheetsClient.updateStaffPreferences(staffId, { [field]: value });
    } catch (err) {
      applyStaffField(staffId, field, !value);
      setPrefsError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSavingPrefsId(null);
    }
  };

  const toggleStaffRegistration = async () => {
    setSaving(true);
    setError('');
    try {
      await sheetsClient.setStaffRegistrationStatus(isStaffRegistrationComplete ? '' : 'Complete');
      await refetch.timeline();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading.staff || loading.timeline || loading.feeConfig || !school) {
    return (
      <div style={{ padding: 20, margin: '0 auto' }}>
        <h1 style={{ fontSize: 22, fontWeight: 500, marginBottom: 4 }}>Teachers & chaperones</h1>
        <Card title="Registered staff & chaperones">
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ display: 'flex', gap: 16, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #E5E5E5' }}>
              <Skeleton width="30%" height={14} />
              <Skeleton width="16%" height={20} radius={20} />
              <Skeleton width="20%" height={14} />
              <Skeleton width="16%" height={20} radius={20} />
            </div>
          ))}
        </Card>
      </div>
    );
  }

  if (selected) {
    return (
      <div style={{ padding: 20, maxWidth: 780, margin: '0 auto' }}>
        <div style={{ marginBottom: 20 }}>
          <Button size="sm" onClick={() => setSelected(null)}>← Back to roster</Button>
        </div>
        <Card
          title={`${selected.firstName} ${selected.lastName}`}
          action={<Badge variant={selected.role === 'Teacher' ? 'purple' : 'neutral'}>{selected.role}</Badge>}
          subtitle="Submitted via staff/chaperone registration form."
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="First name" value={selected.firstName} />
            <Field label="Last name" value={selected.lastName} />
            <Field label="Phone number" value={selected.phone} />
            <Field label="Role" value={selected.role} />
            <Field label="T-shirt size" value={selected.tshirtSize} />
            <Field label="Dietary restrictions" value={selected.dietary} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#666', marginTop: 10 }}>
            <input type="checkbox" checked={selected.mediaReleaseComplete} disabled />
            Media release form completed (read-only — confirmed by their own submission)
          </label>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 20, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 500, marginBottom: 4 }}>Teachers & chaperones</h1>
      <p style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>
        Staff and chaperones must complete the form themselves so the media release is properly signed.</p>
        <p style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>
        *** NOTE: Each school registration fee includes an OSSDF t-shirt and banquet/gala meal for the first {INCLUDED_STAFF_COUNT} teachers/chaperones.
        <br />Additional teachers/chaperones will be charged a fee of {formatCurrency(additionalStaffFee)} to cover these costs.
      </p>

      <PaymentPendingNote locks={locks} what="teachers and chaperones" />

      <TabBar tabs={TABS} activeTab={activeTab} onChange={setActiveTab} counts={{ Roster: staff.length }} />

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
        {activeTab === 'Invite link' && (() => {
          const inviteLink = `${window.location.origin}${process.env.PUBLIC_URL}/registration/staff?school=${encodeURIComponent(school.id)}`;
          // Same rule as POST /api/register/staff: no staff sign-ups until the
          // school has registered for the event, or after it withdraws.
          const newStaffBlocked = locks?.notRegistered || locks?.withdrawn;
          return (
            <>
              {newStaffBlocked && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <LockBadge locks={locks} />
                  <span style={{ fontSize: 13, color: '#666' }}>
                    {locks?.notRegistered
                      ? "Staff and chaperones can't sign up yet — your school hasn't registered for this event. Register for the event first; this link won't accept sign-ups until then."
                      : "Staff and chaperones can't be added — your school has withdrawn from this event."}
                  </span>
                </div>
              )}
              <Card title="Staff & chaperone invite link" subtitle="Share with any teacher or chaperone attending. Each person fills in their own details and signs their own media release.">
                <div style={{ background: '#F5F5F5', borderRadius: 8, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <span style={{ fontSize: 13, color: '#2D2E33', fontFamily: 'monospace', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {inviteLink}
                  </span>
                  <Button size="sm" onClick={() => navigator.clipboard.writeText(inviteLink)} disabled={newStaffBlocked}>Copy</Button>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {(() => {
                    const subject = `${school.name} — Staff & Chaperone Registration`;
                    const body = `Hi,\n\nPlease register as a staff member or chaperone for ${school.name} using the link below. You'll need to fill in your own details and sign the media release yourself.\n\n${inviteLink}\n\nThanks!`;
                    const composeUrls = {
                      Gmail: `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
                      Outlook: `https://outlook.office.com/mail/deeplink/compose?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
                      Default: `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
                    };
                    const openWith = (provider) => {
                      setEmailMenuOpen(false);
                      if (provider === 'Default') {
                        window.location.href = composeUrls.Default;
                      } else {
                        window.open(composeUrls[provider], '_blank', 'noopener,noreferrer');
                      }
                    };
                    return (
                      <div style={{ position: 'relative' }}>
                        <Button size="sm" onClick={() => setEmailMenuOpen((o) => !o)}>Email link ▾</Button>
                        {emailMenuOpen && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 2, background: '#fff', border: '1px solid #D5D5D5', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 10, whiteSpace: 'nowrap' }}>
                            <button onClick={() => openWith('Gmail')} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', fontSize: 13, color: '#2D2E33', background: 'none', border: 'none', cursor: 'pointer' }}>Gmail</button>
                            <button onClick={() => openWith('Outlook')} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', fontSize: 13, color: '#2D2E33', background: 'none', border: 'none', cursor: 'pointer' }}>Outlook</button>
                            <button onClick={() => openWith('Default')} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', fontSize: 13, color: '#2D2E33', background: 'none', border: 'none', cursor: 'pointer' }}>Default mail app</button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </Card>
              <div style={{ background: '#F5F5F5', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#2D2E33' }}>
                Manual entry isn't available here — each person needs to submit the form themselves so their media release is properly signed.
              </div>
            </>
          );
        })()}

        {activeTab === 'Roster' && (
          <>
            <Card title="Registered staff & chaperones" subtitle="Click a row to view full submitted details.">
              {staff.length === 0 ? (
                <EmptyState
                  icon="🧑‍🏫"
                  title="No staff or chaperones yet"
                  description="Share your invite link so teachers and chaperones can register."
                  action={<Button variant="primary" onClick={() => setActiveTab('Invite link')}>Go to invite link</Button>}
                />
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><Th>Name</Th><Th>Role</Th><Th>Phone</Th><Th>T-shirt</Th><Th>Media release</Th><Th>Receive emails</Th><Th>Add to WhatsApp</Th><Th>Workshop supervisor</Th></tr></thead>
                  <tbody>
                    {staff.map((s) => (
                      <tr key={s.id} onClick={() => setSelected(s)} style={{ cursor: 'pointer' }}>
                        <Td>{s.firstName} {s.lastName}</Td>
                        <Td><Badge variant={s.role === 'Teacher' ? 'purple' : 'neutral'}>{s.role}</Badge></Td>
                        <Td>{s.phone || '—'}</Td>
                        <Td>{s.tshirtSize || '—'}</Td>
                        <Td><Badge variant={s.mediaReleaseComplete ? 'success' : s.phone ? 'warning' : 'danger'}>{s.mediaReleaseComplete ? 'Complete' : s.phone ? 'Pending' : 'Not started'}</Badge></Td>
                        <Td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={!!s.receiveEmails}
                            disabled={savingPrefsId === s.id}
                            onChange={(e) => handleTogglePreference(s.id, 'receiveEmails', e.target.checked)}
                          />
                        </Td>
                        <Td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={!!s.addToWhatsapp}
                            disabled={savingPrefsId === s.id}
                            onChange={(e) => handleTogglePreference(s.id, 'addToWhatsapp', e.target.checked)}
                          />
                        </Td>
                        <Td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={!!s.workshopSupervisor}
                            disabled={savingPrefsId === s.id}
                            aria-label={`${s.firstName} ${s.lastName} supervises workshops`}
                            onChange={(e) => handleTogglePreference(s.id, 'workshopSupervisor', e.target.checked)}
                          />
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {prefsError && <div style={{ fontSize: 12, color: '#C0392B', marginTop: 10 }}>{prefsError}</div>}
              {staff.length > 0 && (
                <div style={{ fontSize: 13, marginTop: 12, padding: '10px 12px', borderRadius: 8, background: hasSupervisor ? '#E1F5EE' : '#FAEEDA', color: hasSupervisor ? '#0F6E56' : '#854F0B' }}>
                  {hasSupervisor
                    ? '✓ Workshop supervisor selected. Supervisors help in one workshop in each session — OSSDF will let you know where.'
                    : 'Please tick at least one teacher/chaperone as a workshop supervisor. Supervisors help in one workshop in each session.'}
                </div>
              )}
            </Card>
            <Card title="All staff registrations submitted?">
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Button
                  variant={isStaffRegistrationComplete ? 'primary' : 'default'}
                  disabled={saving || (!isStaffRegistrationComplete && !hasSupervisor)}
                  onClick={() => !isStaffRegistrationComplete && toggleStaffRegistration()}
                >
                  ✓ Registration Complete
                </Button>
                {!isStaffRegistrationComplete && !hasSupervisor && (
                  <span style={{ fontSize: 12, color: '#854F0B', alignSelf: 'center' }}>Tick at least one workshop supervisor above first.</span>
                )}
                {error && <div style={{ fontSize: 12, color: '#C0392B', marginTop: 10 }}>{error}</div>}
              </div>

            </Card>
          </>
        )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: 13, color: '#666', marginBottom: 5, display: 'block' }}>{label}</label>
      <input style={{ width: '100%', padding: '8px 12px', border: '1px solid #D5D5D5', borderRadius: 8, fontSize: 14, fontFamily: 'inherit' }} defaultValue={value || ''} />
    </div>
  );
}
function Th({ children }) { return <th style={{ textAlign: 'left', fontSize: 12, color: '#666', fontWeight: 500, padding: '6px', borderBottom: '1px solid #E5E5E5' }}>{children}</th>; }
function Td({ children, style, onClick }) { return <td onClick={onClick} style={{ fontSize: 13, padding: '8px 6px', borderBottom: '1px solid #E5E5E5', ...style }}>{children}</td>; }
